/**
 * EXTRATO DE VIAGEM NO APARELHO (tarefa 2F) — despesas e vales entre o gestor
 * e cada comprador. Fora do custo dos lotes, de propósito (decisão do gestor).
 *
 * Mesmo jeito do armazém das fichas: guarda no localStorage, por pessoa (a
 * chave leva o id de quem entrou), e o que foi lançado aqui fica PENDENTE até
 * o «Sincronizar». O comprador lança a despesa sem sinal, no curral ou na
 * estrada; a foto do comprovante fica no IndexedDB (`arquivos.ts`) com o mesmo
 * id que vai ao banco — a tarefa 2G sobe o arquivo usando esse id.
 *
 * Nada se apaga: lançamento errado é ANULADO com motivo, e a anulação também
 * entra na fila. O que o servidor recusa de vez sai da fila e fica aqui
 * marcado «recusado pelo servidor», riscado e fora das somas.
 *
 * Comissão não mora aqui: o aparelho do comprador nunca recebe comissão. A
 * do gestor chega só no retrato de cada acerto (`Acerto.comissoes`) e na
 * prévia pedida ao servidor na hora.
 */
import { useSyncExternalStore } from 'react'
import { lerDono } from './armazem'
import { api, Recusa, SemSinal, type Eu } from './sessao'
import { resumir, valoresQueMudaram, type Acerto, type Lancamento, type PorConta, type TotaisDoAcerto } from '../dominio/extrato'
import { porContaPadrao } from '../dominio/ferramentas'

// tipos e contas moram em src/dominio/extrato.ts (testados em testes/contas.mjs)
export type { Acerto, Anulacao, Lancamento, Parcial, PorConta, Resumo, TipoDeLancamento, TotaisDoAcerto } from '../dominio/extrato'
export { emAberto, estaAcertado, resumir, saldoDoAcerto } from '../dominio/extrato'

export interface CompradorDoExtrato { id: string; nome: string; ativa: boolean }

export interface EstadoDoExtrato {
  lancamentos: Lancamento[]
  acertos: Acerto[]
  compradores: CompradorDoExtrato[]
  /** ids de lançamentos lançados ou anulados aqui que ainda não subiram */
  pendentes: string[]
  ultimaSync: string | null
}

export const CATEGORIAS_DE_DESPESA = ['Almoço', 'Hospedagem', 'Combustível', 'Borracharia', 'Assistência', 'Outra']

const PREFIXO = 'pesagem.extrato.v1.'

function vazio(): EstadoDoExtrato {
  return { lancamentos: [], acertos: [], compradores: [], pendentes: [], ultimaSync: null }
}

function completar(e: Partial<EstadoDoExtrato> | null): EstadoDoExtrato {
  if (!e || typeof e !== 'object') return vazio()
  return {
    lancamentos: Array.isArray(e.lancamentos) ? e.lancamentos : [],
    acertos: Array.isArray(e.acertos) ? e.acertos : [],
    compradores: Array.isArray(e.compradores) ? e.compradores : [],
    pendentes: Array.isArray(e.pendentes) ? e.pendentes : [],
    ultimaSync: e.ultimaSync ?? null,
  }
}

let donoAberto: string | null = null
let estado = vazio()
const ouvintes = new Set<() => void>()

/**
 * Os dados são de quem entrou. O armazém das fichas já sabe quem é (abrirDoDono);
 * aqui basta conferir a cada leitura e trocar quando a pessoa mudou.
 */
function garantirDono(): void {
  const id = lerDono()?.id ?? null
  if (id === donoAberto) return
  donoAberto = id
  try {
    const t = id ? localStorage.getItem(PREFIXO + id) : null
    estado = completar(t ? JSON.parse(t) : null)
  } catch {
    estado = vazio()
  }
}

export function usarExtrato(): EstadoDoExtrato {
  return useSyncExternalStore(
    (f) => { ouvintes.add(f); return () => ouvintes.delete(f) },
    () => { garantirDono(); return estado },
  )
}

export function lerExtrato(): EstadoDoExtrato {
  garantirDono()
  return estado
}

/** Toda alteração passa por aqui. `pendente` põe o lançamento na fila do «Sincronizar». */
function mudarExtrato(fn: (e: EstadoDoExtrato) => void): void {
  garantirDono()
  const novo = structuredClone(estado)
  fn(novo)
  estado = novo
  try { if (donoAberto) localStorage.setItem(PREFIXO + donoAberto, JSON.stringify(estado)) } catch { /* sem disco: a tela das fichas já avisa */ }
  ouvintes.forEach((f) => f())
}

/* ---------- lançar e anular (com ou sem sinal) ---------- */

/**
 * 3C · De quem é, por padrão, a despesa nova deste comprador: vem do vínculo
 * dele (contratado → da empresa; comissionado → dele). Sem vínculo, `null`, e
 * o formulário pergunta. É só o padrão: a pessoa troca na hora, e as regras do
 * banco (005) não mudam. Vale sem sinal: o vínculo vem guardado no «eu» — o do
 * próprio comprador, ou o mapa da equipe que só o gestor recebe.
 */
export function porContaInicial(eu: Eu, compradorId: string): PorConta | null {
  const vinculo = eu.pessoa.id === compradorId ? eu.pessoa.vinculo : eu.vinculos?.[compradorId]
  return porContaPadrao(vinculo)
}

export function lancar(l: Lancamento): void {
  mudarExtrato((e) => {
    e.lancamentos.push(l)
    if (!e.pendentes.includes(l.id)) e.pendentes.push(l.id)
  })
}

export function anularLancamento(id: string, motivo: string, porId: string): void {
  mudarExtrato((e) => {
    const l = e.lancamentos.find((x) => x.id === id)
    if (!l || l.anulacao) return
    l.anulacao = { em: new Date().toISOString(), por: porId, motivo }
    if (!e.pendentes.includes(id)) e.pendentes.push(id)
  })
}

/* ---------- sincronizar (chamado pelo «Sincronizar» de fila.ts) ---------- */

export interface ContagemDoExtrato { subiram: number; desceram: number; recusas: string[] }

/**
 * Sobe os pendentes e desce o que mudou. Sem sinal: lança `SemSinal` (a fila
 * para tudo, nada se perde). Recusa do servidor vira frase na tela e NÃO
 * derruba a sincronização das fichas — inclusive se o banco ainda não tiver
 * a migração 005.
 */
export async function sincronizarExtrato(r: ContagemDoExtrato): Promise<void> {
  if (!lerDono()) return
  try {
    await subir(r)
    await descer(r)
  } catch (e) {
    if (e instanceof Recusa) { r.recusas.push(`Extrato: ${e.message}`); return }
    throw e
  }
}

async function subir(r: ContagemDoExtrato): Promise<void> {
  for (const id of [...lerExtrato().pendentes]) {
    const l = lerExtrato().lancamentos.find((x) => x.id === id)
    if (!l) { tirarDaFila(id); continue }
    try {
      await api('extrato/lancamentos', {
        id: l.id, comprador_id: l.compradorId, tipo: l.tipo, data: l.data, valor: l.valor, categoria: l.categoria,
        descricao: l.descricao, por_conta: l.porConta, comprovante_id: l.comprovanteId,
        anulacao: l.anulacao ? { motivo: l.anulacao.motivo } : undefined,
      })
      mudarExtrato((e) => {
        // só sai da fila se ninguém anulou enquanto subia
        const x = e.lancamentos.find((y) => y.id === id)
        if (!x || !!x.anulacao === !!l.anulacao) e.pendentes = e.pendentes.filter((y) => y !== id)
      })
      r.subiram++
    } catch (e) {
      if (!(e instanceof Recusa)) throw e
      const frase = `${l.tipo === 'adiantamento' ? 'Vale' : 'Despesa'} de ${l.data.split('-').reverse().join('/')}: ${e.message}`
      if (e.status === 409 && e.dados?.['codigo'] === 'nao_anulou') {
        // a anulação não valeu (ex.: já acertado): o lançamento volta a valer aqui
        mudarExtrato((x) => {
          const y = x.lancamentos.find((z) => z.id === id)
          if (y) y.anulacao = null
          x.pendentes = x.pendentes.filter((z) => z !== id)
        })
      } else if (recusaDeVez(e)) {
        // Mandar de novo daria a mesma resposta para sempre (ex.: o gestor lançou
        // para quem deixou de ser comprador): o contador nunca zeraria e o
        // «Marcar acertado» ficaria travado. Sai da fila e fica aqui riscado,
        // com a frase do servidor — nada se apaga, e fica fora das somas.
        mudarExtrato((x) => {
          const y = x.lancamentos.find((z) => z.id === id)
          if (y) y.recusa = { em: new Date().toISOString(), motivo: e.message }
          x.pendentes = x.pendentes.filter((z) => z !== id)
        })
        r.recusas.push(`${frase} — não entrou no extrato.`)
        continue
      }
      r.recusas.push(frase)
    }
  }
}

/**
 * Recusa definitiva: o servidor entendeu o pedido e disse não (4xx). Não
 * contam o 401 (crachá vencido: a pessoa entra de novo e manda) nem os 5xx
 * (servidor ou banco fora do ar: tenta de novo no próximo Sincronizar).
 */
function recusaDeVez(e: Recusa): boolean {
  return e.status >= 400 && e.status < 500 && e.status !== 401
}

async function descer(r: ContagemDoExtrato): Promise<void> {
  const desde = lerExtrato().ultimaSync
  const q = desde ? `?desde=${encodeURIComponent(desde)}` : ''
  const d = await api<{ agora: string; lancamentos: Lancamento[]; acertos: Acerto[]; compradores: CompradorDoExtrato[] }>(`extrato${q}`)
  mudarExtrato((e) => {
    for (const novo of d.lancamentos ?? []) {
      if (e.pendentes.includes(novo.id)) continue // a mudança daqui ainda vai subir
      const i = e.lancamentos.findIndex((x) => x.id === novo.id)
      // o nome do arquivo só existe no aparelho que tirou a foto: não perder
      const comFoto = i >= 0 && e.lancamentos[i]!.comprovante ? { ...novo, comprovante: e.lancamentos[i]!.comprovante } : novo
      if (i >= 0) e.lancamentos[i] = comFoto
      else e.lancamentos.push(comFoto)
      r.desceram++
    }
    if (Array.isArray(d.acertos)) e.acertos = d.acertos
    if (Array.isArray(d.compradores)) e.compradores = d.compradores
    e.ultimaSync = d.agora || e.ultimaSync
  })
}

function tirarDaFila(id: string): void {
  mudarExtrato((e) => { e.pendentes = e.pendentes.filter((y) => y !== id) })
}

/**
 * R1A · «Sair»: fica só o que ainda não subiu (nada se perde); despesas,
 * vales e acertos que já estão no banco saem do aparelho e voltam inteiros no
 * próximo «Sincronizar».
 */
export function enxugarExtratoAoSair(): void {
  garantirDono()
  if (!donoAberto) return
  mudarExtrato((e) => {
    e.lancamentos = e.lancamentos.filter((l) => e.pendentes.includes(l.id))
    e.acertos = []
    e.compradores = []
    e.ultimaSync = null
  })
}

/* ---------- só gestor, só com sinal ---------- */

/**
 * Marca «acertado»: primeiro sincroniza (o que está aqui tem de estar lá),
 * depois fecha no servidor e desce de novo. O acerto leva tudo o que está em
 * aberto com data até o «até» — inclusive o de antes do «de».
 * Só trava por pendência DESTE comprador: lançamento de outro que não subiu
 * não muda o acerto deste.
 */
export async function marcarAcertado(p: {
  id: string; compradorId: string; de: string; ate: string; obs: string; comprovanteId: string | null
  /** R1A · os totais que a tela mostrou */
  mostrado: TotaisDoAcerto
  /** R1A · opcionais: o pagamento feito junto com o acerto */
  valorPago?: number | null; pagoEm?: string | null
}): Promise<{ lancamentos: number }> {
  const c: ContagemDoExtrato = { subiram: 0, desceram: 0, recusas: [] }
  await subir(c)
  // R1A: desce antes de conferir — o que outro aparelho mandou entra na conta
  await descer(c)
  const e = lerExtrato()
  const presos = e.pendentes.filter((id) => e.lancamentos.find((l) => l.id === id)?.compradorId === p.compradorId)
  if (presos.length) {
    const quantos = presos.length === 1 ? 'um lançamento deste comprador que não subiu' : `${presos.length} lançamentos deste comprador que não subiram`
    const porque = c.recusas.length ? `\n\n${c.recusas.join('\n')}` : ''
    throw new Recusa(`Há ${quantos} neste aparelho. Sincronize antes de acertar.${porque}`, 409)
  }
  // R1A · O acerto não fecha valor que a tela não mostrou: refaz a conta com o
  // que acabou de descer e pede a prévia de novo; se mudou (outro aparelho
  // mandou uma despesa no meio), para — a tela já mostra os valores novos.
  // O servidor confere mais uma vez, na mesma gravação do acerto.
  const r = resumir(e.lancamentos.filter((l) => l.compradorId === p.compradorId), e.acertos, p.de, p.ate).aAcertar
  const previa = await previaDeComissoes(p.compradorId, p.de, p.ate)
  if (!previa) throw new SemSinal('Sem sinal para conferir as comissões.')
  const agora: TotaisDoAcerto = { despesas: r.aRessarcir, adiantamentos: r.adiantamentos, comissoes: previa.total, saldoAnterior: previa.saldoAnterior }
  const mudou = valoresQueMudaram(p.mostrado, agora)
  if (mudou.length) throw new Recusa(`Os valores mudaram, confira:\n${mudou.join('\n')}`, 409, { codigo: 'valores_mudaram' })
  const resp = await api<{ lancamentos: number }>('extrato/acertos', {
    id: p.id, comprador_id: p.compradorId, de: p.de, ate: p.ate, obs: p.obs, comprovante_id: p.comprovanteId,
    esperado: { despesas: agora.despesas, adiantamentos: agora.adiantamentos, comissoes: agora.comissoes, saldo_anterior: agora.saldoAnterior },
    valor_pago: p.valorPago ?? null, pago_em: p.valorPago ? p.pagoEm ?? null : null,
  })
  await descer(c)
  return resp
}

/**
 * R1A · Registrar o pagamento de um acerto já feito (gestor ou Consulta —
 * é «baixa»). Só com sinal, como o acerto. O comprovante, se houver, fica no
 * aparelho e sobe pela fila de arquivos (2G).
 */
export async function registrarPagamentoDoAcerto(id: string, valor: number, pagoEm: string, comprovanteId: string | null): Promise<void> {
  await api('extrato/acertos/pagamento', { id, valor_pago: valor, pago_em: pagoEm, comprovante_id: comprovanteId })
  await descer({ subiram: 0, desceram: 0, recusas: [] })
}

export async function anularAcerto(id: string, motivo: string): Promise<void> {
  await api('extrato/acertos/anular', { id, motivo })
  await descer({ subiram: 0, desceram: 0, recusas: [] })
}

/**
 * Comissões em aberto até o «até»: o total (vai para o acerto) e a parte de
 * fichas de antes do «de». R1A: o saldo negativo do acerto anterior, e os
 * avisos de ficha cujas cabeças mudaram depois de lançada a comissão.
 */
export interface PreviaDeComissoes { total: number; antes: number; saldoAnterior: number; avisos: string[] }

/**
 * Prévia das comissões (só gestor/consulta, só com sinal). A soma mora no
 * servidor (`comissoesEmAberto` em servidor/extrato.ts — o ponto de ligação
 * com a 2D), com a mesma regra do acerto. `undefined` = sem sinal.
 */
export async function previaDeComissoes(compradorId: string, de: string, ate: string): Promise<PreviaDeComissoes | undefined> {
  try {
    const r = await api<{ comissoes: number; antes?: number; saldo_anterior?: number; avisos?: string[] }>(
      `extrato/comissoes?comprador=${encodeURIComponent(compradorId)}&de=${de}&ate=${ate}`)
    return {
      total: Number(r.comissoes) || 0, antes: Number(r.antes) || 0, saldoAnterior: Number(r.saldo_anterior) || 0,
      avisos: Array.isArray(r.avisos) ? r.avisos.map(String) : [],
    }
  } catch (e) {
    if (e instanceof SemSinal || e instanceof Recusa) return undefined
    throw e
  }
}
